import type { RadioPlanAssignment } from '@/mocks/superAdminPlans';
import { planDefinitions, getPlanById, getPlanName } from '@/mocks/superAdminPlans';
import { Link } from 'react-router-dom';
import StorageBar from './StorageBar';

interface PlanAssignmentsTableProps {
  assignments: RadioPlanAssignment[];
  onChangePlan: (radio: RadioPlanAssignment) => void;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function PlanBadge({ planId }: { planId: string }) {
  const def = getPlanById(planId);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap"
      style={{
        backgroundColor: def ? `${def.color}14` : '#f5f5f5',
        color: def?.color ?? '#78716c',
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: def?.color ?? '#78716c' }}></span>
      {def?.name ?? 'Desconocido'}
    </span>
  );
}

function StatusBadge({ status }: { status: RadioPlanAssignment['status'] }) {
  const map = {
    active: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Activo' },
    trial: { bg: 'bg-sky-50', text: 'text-sky-700', label: 'Trial' },
    expired: { bg: 'bg-red-50', text: 'text-red-700', label: 'Expirado' },
  };
  const s = map[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${s.bg} ${s.text} text-[10px] font-medium`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'active' ? 'bg-emerald-500' : status === 'trial' ? 'bg-sky-500' : 'bg-red-500'}`}></span>
      {s.label}
    </span>
  );
}

export default function PlanAssignmentsTable({ assignments, onChangePlan }: PlanAssignmentsTableProps) {
  if (assignments.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-stack-line text-foreground-300 text-2xl"></i>
        </div>
        <p className="text-foreground-400 text-sm font-medium">No se encontraron radios</p>
        <p className="text-foreground-300 text-xs mt-1">Probá cambiando los filtros</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Plan</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Almacenamiento</th>
                <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Imágenes</th>
                <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Videos</th>
                <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Posts</th>
                <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Oyentes/mes</th>
                <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                <th className="w-12 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((radio, idx) => (
                <tr
                  key={radio.id}
                  className="border-b border-background-200/60 hover:bg-background-50 transition-colors"
                  style={{ animation: `fadeInUp 0.2s ease-out ${idx * 0.03}s both` }}
                >
                  <td className="px-4 py-3">
                    <div>
                      <Link to={`/sa/radios?search=${encodeURIComponent(radio.radioName)}`} className="text-foreground-800 text-xs font-semibold hover:text-primary-500 transition-colors cursor-pointer">{radio.radioName}</Link>
                      <p className="text-foreground-400 text-[10px]">{radio.frequency}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <PlanBadge planId={radio.planId} />
                    {getPlanById(radio.planId)?.price !== 0 && (
                      <p className="text-foreground-300 text-[10px] mt-0.5">Vence {radio.expiryDate}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="max-w-[160px]">
                      <StorageBar usedGB={radio.storageUsedGB} totalGB={radio.storageTotalGB} compact />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-mono font-semibold ${radio.imagesCount > 0 ? 'text-foreground-800' : 'text-foreground-300'}`}>
                      {radio.imagesCount > 0 ? formatNumber(radio.imagesCount) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-mono font-semibold ${radio.videosCount > 0 ? 'text-foreground-800' : 'text-foreground-300'}`}>
                      {radio.videosCount > 0 ? formatNumber(radio.videosCount) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-mono font-semibold ${radio.postsCount > 0 ? 'text-foreground-800' : 'text-foreground-300'}`}>
                      {radio.postsCount > 0 ? formatNumber(radio.postsCount) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-foreground-800 text-xs font-mono font-semibold">{formatNumber(radio.listenersThisMonth)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={radio.status} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onChangePlan(radio)}
                      className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                      title="Cambiar plan"
                    >
                      <i className="ri-arrow-left-right-line text-foreground-500 text-sm"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-2">
        {assignments.map((radio, idx) => (
          <div
            key={radio.id}
            className="bg-background-50 border border-background-200 rounded-2xl p-4"
            style={{ animation: `fadeInUp 0.2s ease-out ${idx * 0.03}s both` }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-foreground-800 text-xs font-semibold">{radio.radioName}</p>
                <p className="text-foreground-400 text-[10px]">{radio.frequency}</p>
              </div>
              <div className="flex items-center gap-2">
                <PlanBadge planId={radio.planId} />
                <button
                  onClick={() => onChangePlan(radio)}
                  className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                >
                  <i className="ri-arrow-left-right-line text-foreground-500 text-xs"></i>
                </button>
              </div>
            </div>

            <div className="mb-3">
              <StorageBar usedGB={radio.storageUsedGB} totalGB={radio.storageTotalGB} />
            </div>

            <div className="grid grid-cols-4 gap-2 text-center pt-3 border-t border-background-200">
              <div>
                <p className="text-foreground-800 text-xs font-mono font-bold">{radio.imagesCount > 0 ? formatNumber(radio.imagesCount) : '—'}</p>
                <p className="text-foreground-400 text-[9px]">Fotos</p>
              </div>
              <div>
                <p className="text-foreground-800 text-xs font-mono font-bold">{radio.videosCount > 0 ? formatNumber(radio.videosCount) : '—'}</p>
                <p className="text-foreground-400 text-[9px]">Videos</p>
              </div>
              <div>
                <p className="text-foreground-800 text-xs font-mono font-bold">{radio.postsCount > 0 ? formatNumber(radio.postsCount) : '—'}</p>
                <p className="text-foreground-400 text-[9px]">Posts</p>
              </div>
              <div>
                <p className="text-foreground-800 text-xs font-mono font-bold">{formatNumber(radio.listenersThisMonth)}</p>
                <p className="text-foreground-400 text-[9px]">Oyentes</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <StatusBadge status={radio.status} />
              {getPlanById(radio.planId)?.price !== 0 && (
                <span className="text-foreground-300 text-[10px]">Vence {radio.expiryDate}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}